import { t } from "../contract/strings";

/**
 * The stages between pressing Stop and seeing a transcript.
 *
 * Named because the wait is real and mostly on this device: [M] one ECAPA pass over the final
 * stretch measured 1 s on one handset and 32 s on another. A single status line made that
 * read as a frozen button. `score` is the only stage whose remaining work is actually known,
 * so it is the only one with a percentage -- the rest show an indeterminate bar rather than
 * an invented number.
 *
 * The order is the order the work happens in, and the first entry is load-bearing: [M]
 * telling the other devices last left two of them recording for 24.2 s and 22.6 s after their
 * owner had stopped, and everything they captured in that window belonged to nobody.
 *
 * Its own module so the ordering rule can be tested without pulling in a recorder.
 */
export const WRAP_STEPS: Array<[key: string, label: string]> = [
  ["close", t("wrap.close")],
  ["score", t("wrap.score")],
  ["upload", t("wrap.upload")],
  // Between upload and report on purpose (#170-B). It is a real wait with a real
  // consequence: until this lands the server counts this device as still writing, and
  // the merge waits for everyone. Naming it is the difference between "the app is stuck"
  // and "your device is finishing the last of your claims" -- and the step *after* it,
  // "Saying you are done", is only truthful once this one has.
  //
  // Skipped entirely on the legacy path, where nothing publishes a marker. `wrapStep` is
  // keyed by name and monotonic, so a session that never enters delta mode simply moves
  // from upload to report and this entry is never current.
  ["watermark", t("wrap.watermark")],
  ["report", t("wrap.report")],
  // Between report and merge, and only ever current on a device that stopped before the
  // creator did (#286). This device is finished; the merge has not started and *cannot*
  // start, because the conversation is still going on somebody else's device. Saying
  // "Preparing your meeting notes" through that window was the whole ticket:
  // it names work nobody is doing, on a card whose other steps are all this device's own.
  //
  // Driven by the `session_state` on `GET /result`'s 202, which after Stop is the only
  // live channel this device has left -- the state poll, the SSE reader and the audio
  // socket are all closed by then, in that order, before the report goes out.
  //
  // Skipped exactly as `watermark` is skipped on the legacy path: `wrapStep` is keyed by
  // name and monotonic, so a device that never waits moves from report straight to merge
  // and this entry is never current. The label names the dependency rather than the
  // mechanism, because "waiting" without "for whom" is the spinner it replaces -- the
  // status line beside it carries the names.
  ["waiting", t("wrap.waiting")],
  ["merge", t("wrap.merge")],
];

export function wrapIndex(key: string): number {
  return WRAP_STEPS.findIndex(([k]) => k === key);
}

/**
 * Monotonic on purpose. A progress callback belonging to a stage can land after that stage
 * has been left -- the final scoring pass reports fractions while `upload` is already
 * current -- and a list that walked backwards would read as the app undoing itself.
 */
export function nextWrapAt(current: number, key: string): number {
  const index = wrapIndex(key);
  if (index < 0) return current;
  return Math.max(current, index);
}
